<script setup>
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { watchDebounced } from "@vueuse/core";
  import { Info, Trash, Plus, Copy } from "lucide-vue-next";
  import { useToastsStore } from "@/stores/toastsStore";

  import { DateTimePicker } from "@/components/episto-ui/date-time-picker";

  import { isEmpty } from "@utils/toolBox";
  import EmailCampaignConditions from "@/components/Mailing/EmailCampaignConditions.vue";

  import OnePanePage from "@/templates/OnePanePage.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Banner } from "@/components/episto-ui/banner";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { Textarea } from "@/components/ui/textarea";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
  import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
  } from "@/components/ui/alert-dialog";

  import { useMailingListsStore } from "@stores/survey/mailing/mailingLists";
  import { useEmailCampaignStore } from "@stores/survey/mailing/emailCampaign";
  import { useTemplatesStore } from "@stores/mailing/templates";
  import { useContactsStore } from "@stores/survey/mailing/contacts";
  import { useSurveyStore } from "@stores/survey/survey";

  const emailCampaignStore = useEmailCampaignStore();
  const {
    emailCampaign,
    campaignListAttributes,
    isNewCampaign,
    getCurrentCampaignUrlParams,
    hasCampaignChanged,
    isCampaignEditable,
    isBatchCampaign,
    isTransactionalCampaign,
    hasScheduledDate,
    isSuspendedCampaign,
    getCountryOptions,
    getLanguageOptions,
  } = storeToRefs(emailCampaignStore);
  const {
    addCampaignUrlParam,
    removeCampaignUrlParam,
    updateEmailCampaign,
    createEmailCampaign,
    testEmailCampaign,
    scheduleEmailCampaign,
    pushEmailCampaign,
    estimateEmailCampaignAudience,
  } = emailCampaignStore;
  const { lightMailingLists } = storeToRefs(useMailingListsStore());
  const { templateOptions } = storeToRefs(useTemplatesStore());
  const { survey } = storeToRefs(useSurveyStore());
  const { areContactsEmpty, getContactOptions } = storeToRefs(useContactsStore());

  const isTestDialogVisible = ref(false);
  const isSurveyNotLiveDialogVisible = ref(false);
  const testContactToken = ref(null);

  const toast = useToastsStore();
  const router = useRouter();

  const getCampaignHeaderTitle = computed(() =>
    isNewCampaign.value ? "New campaign" : `Edit ${emailCampaign.value?.name}`,
  );
  const getMailingListOptions = computed(() =>
    lightMailingLists.value.map((l) => ({ value: l.id, label: l.name, shared: l.shared })),
  );

  const hasObseleteAttributeValues = (value, name) => {
    const attributeValues = campaignListAttributes.value?.[name];
    if (attributeValues == "open_ended_attribute") return false;
    if (Array.isArray(value)) return value.some((v) => !attributeValues.includes(v));
    return !attributeValues.includes(value);
  };

  // --- Validation ---

  const validationSchema = toTypedSchema(
    z
      .object({
        name: z.string({ required_error: "Please input the campaign name" }).min(1, "Please input the campaign name"),
        subject: z.string({ required_error: "Please input the subject line" }).min(1, "Please input the subject line"),
        country: z.string({ required_error: "Please select a country" }).min(1, "Please select a country"),
        language: z.string({ required_error: "Please select a language" }).min(1, "Please select a language"),
        delivery_mode: z.enum(["batch", "transactional"]),
        mailing_list_id: z.union([z.string(), z.number()]).optional().nullable(),
        template_id: z.union([z.string(), z.number()], { required_error: "Please select a template" }),
        sender_name: z.string().optional().nullable(),
        scheduled_at: z.string().optional().nullable(),
        conditions: z.array(z.any()).optional(),
      })
      .superRefine((data, ctx) => {
        if (data.delivery_mode === "batch" && !data.mailing_list_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please select a mailing list",
            path: ["mailing_list_id"],
          });
        }

        if (data.scheduled_at) {
          const scheduledDate = new Date(data.scheduled_at);
          const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
          if (scheduledDate <= fiveMinutesFromNow) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Please schedule more than 5 minutes ahead",
              path: ["scheduled_at"],
            });
          }
        }

        for (const condition of data.conditions ?? []) {
          if (isEmpty(condition.value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "All conditions must have a value",
              path: ["conditions"],
            });
            break;
          }
          if (condition.type === "bucket" && !condition.relation) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "All bucket conditions must have a relation",
              path: ["conditions"],
            });
            break;
          }
          if (
            condition.type === "custom_attribute" &&
            !Object.keys(campaignListAttributes.value ?? {}).includes(condition.name)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Conditions have obsolete attributes",
              path: ["conditions"],
            });
            break;
          }
          if (condition.type === "custom_attribute" && hasObseleteAttributeValues(condition.value, condition.name)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Conditions have obsolete values",
              path: ["conditions"],
            });
            break;
          }
        }
      }),
  );

  const { handleSubmit, meta, values } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: {
      name: emailCampaign.value?.name || "",
      subject: emailCampaign.value?.subject || "",
      country: emailCampaign.value?.country || "",
      language: emailCampaign.value?.language || "",
      delivery_mode: emailCampaign.value?.delivery_mode || "batch",
      mailing_list_id: emailCampaign.value?.mailing_list_id || null,
      template_id: emailCampaign.value?.template_id || null,
      sender_name: emailCampaign.value?.sender_name || "",
      scheduled_at: emailCampaign.value?.scheduled_at || "",
      conditions: emailCampaign.value?.conditions || [],
    },
  });

  const hasErrors = computed(() => meta.value.dirty && !meta.value.valid);

  watch(values, (v) => Object.assign(emailCampaign.value, v), { deep: true });

  const onSubmit = handleSubmit(async () => {
    if (isNewCampaign.value) await createEmailCampaign();
    else await updateEmailCampaign();
  });

  const handleTest = () => {
    if (areContactsEmpty.value) {
      sendTest();
    } else {
      testContactToken.value = getContactOptions.value[0].value;
      isTestDialogVisible.value = true;
    }
  };

  const sendTest = async () => {
    isTestDialogVisible.value = false;
    await testEmailCampaign(testContactToken.value);
    toast.info("Your test email is on its way and should arrive within the next minute.");
  };

  const navigateToCampaigns = () => router.push({ name: "SurveyEmailCampaigns" });

  const handleSchedule = async () => {
    await scheduleEmailCampaign();
    if (["draft", "review"].includes(survey.value.status)) {
      isSurveyNotLiveDialogVisible.value = true;
    } else {
      navigateToCampaigns();
    }
  };

  const handlePush = async () => {
    await pushEmailCampaign();
    toast.info("Your campaign will be pushed to Brevo shortly.");
  };

  const copyTransactionalEmailApiEndpoint = () => {
    navigator.clipboard.writeText(emailCampaign.value.transactional_email_api_endpoint);
    toast.info("Copied to clipboard");
  };

  const mailingList = computed(() => emailCampaign.value.mailing_list_id);

  watchDebounced(mailingList, async () => await estimateEmailCampaignAudience(), { debounce: 300 });
</script>

<template>
  <OnePanePage>
    <Card
      data-cy="survey-email-campaigns-new-campaign-page survey-email-campaigns-edit-campaign-page"
      class="max-w-3xl mx-auto p-6">
      <CardHeader class="pb-6">
        <CardTitle class="heading-page">{{ getCampaignHeaderTitle }}</CardTitle>
        <Banner
          v-if="!isCampaignEditable"
          variant="info">
          Adjustments to a campaign can be made only when it's in <strong>draft</strong>, <strong>error</strong> or
          <strong>suspended</strong> mode
        </Banner>
      </CardHeader>
      <CardContent class="space-y-6">
        <FormField
          v-slot="{ componentField }"
          name="name">
          <FormItem>
            <FormLabel>Name <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                :disabled="!isCampaignEditable" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField
          v-slot="{ componentField }"
          name="delivery_mode">
          <FormItem>
            <FormLabel>Delivery Mode <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Tabs
                v-bind="componentField"
                class="w-full">
                <TabsList class="w-full">
                  <TabsTrigger value="batch">Batch</TabsTrigger>
                  <TabsTrigger value="transactional">Transactional</TabsTrigger>
                </TabsList>
              </Tabs>
            </FormControl>
          </FormItem>
        </FormField>
        <div
          v-if="isBatchCampaign"
          class="space-y-4">
          <div class="flex items-center gap-2">
            <h4 class="heading-section">Recipients</h4>
            <Tooltip
              v-if="!isNewCampaign"
              content="The audience might change depending on the evolution of respondent status during the data collection period">
              <Badge variant="secondary">
                Audience:
                {{ emailCampaign.statistics?.targeted?.toLocaleString() || emailCampaign.contacts_count || 0 }}
              </Badge>
            </Tooltip>
          </div>
          <FormField
            v-slot="{ componentField }"
            name="mailing_list_id">
            <FormItem>
              <FormLabel>List <span class="text-destructive">*</span></FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="getMailingListOptions"
                  filterable
                  :disabled="!isCampaignEditable"
                  placeholder="Select a list" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField name="conditions">
            <FormItem>
              <FormLabel>Conditions</FormLabel>
              <EmailCampaignConditions />
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div class="space-y-4">
          <div><h4 class="heading-section">Link</h4></div>
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 items-start">
            <FormField
              v-slot="{ componentField }"
              name="country">
              <FormItem>
                <FormLabel>Country <span class="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Select
                    v-bind="componentField"
                    :options="getCountryOptions"
                    :disabled="!isCampaignEditable"
                    placeholder="Select a country" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <FormField
              v-slot="{ componentField }"
              name="language">
              <FormItem>
                <FormLabel>Language <span class="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Select
                    v-bind="componentField"
                    :options="getLanguageOptions"
                    :disabled="!isCampaignEditable"
                    placeholder="Select a language" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
          <FormItem>
            <Label>URL Params</Label>
            <div
              v-if="getCurrentCampaignUrlParams.length > 0"
              class="grid grid-cols-[1fr_1fr_auto] gap-2 text-sm font-medium text-muted-foreground">
              <span>Name</span>
              <span>Value</span>
              <span class="w-8"></span>
            </div>
            <div
              v-for="(item, index) in getCurrentCampaignUrlParams"
              :key="`url-param-${index}`"
              class="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Input
                v-model="item.name"
                placeholder="z"
                :disabled="!isCampaignEditable" />
              <Input
                v-model="item.value"
                placeholder="1"
                :disabled="!isCampaignEditable" />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                :disabled="!isCampaignEditable"
                @click="removeCampaignUrlParam(index)">
                <Trash
                  :stroke-width="1.5"
                  class="size-3.5" />
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              class="w-fit"
              :disabled="!isCampaignEditable"
              @click="addCampaignUrlParam">
              <Plus
                :stroke-width="1.5"
                class="size-3.5 mr-1" />
              Add Url Param
            </Button>
          </FormItem>
        </div>
        <div class="space-y-4">
          <div><h4 class="heading-section">Content</h4></div>
          <FormField
            v-slot="{ componentField }"
            name="subject">
            <FormItem>
              <FormLabel>Subject line <span class="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea
                  v-bind="componentField"
                  :disabled="!isCampaignEditable" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-slot="{ componentField }"
            name="sender_name">
            <FormItem>
              <FormLabel>Sender name</FormLabel>
              <FormControl>
                <Input
                  v-bind="componentField"
                  :disabled="!isCampaignEditable" />
              </FormControl>
            </FormItem>
          </FormField>
          <FormField
            v-slot="{ componentField }"
            name="template_id">
            <FormItem>
              <FormLabel>Template <span class="text-destructive">*</span></FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="templateOptions"
                  filterable
                  :disabled="!isCampaignEditable"
                  placeholder="Select a template" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div
          v-if="isBatchCampaign"
          class="space-y-4">
          <div><h4 class="heading-section">Scheduling</h4></div>
          <FormField
            v-slot="{ componentField }"
            name="scheduled_at">
            <FormItem>
              <FormLabel>Launch date</FormLabel>
              <FormControl>
                <DateTimePicker
                  v-bind="componentField"
                  :disabled="!isCampaignEditable"
                  time-picker />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <FormItem v-if="isTransactionalCampaign && !isNewCampaign">
          <Label class="flex items-center gap-1.5">
            Transactional Email API Endpoint
            <Tooltip side="top">
              <template #content>
                For more information, please refer to the
                <a
                  href="https://episto.slite.page/p/VNji2ulR1YXs65/Transaction-Emails-API"
                  target="_blank"
                  class="underline"
                  >documentation</a
                >
              </template>
              <Info
                :stroke-width="1.5"
                class="size-3.5" />
            </Tooltip>
          </Label>
          <div class="flex items-center gap-0">
            <Badge
              variant="secondary"
              class="rounded-r-none border-r-0 px-3 py-2 text-xs font-semibold">
              POST
            </Badge>
            <div class="relative flex-1">
              <Input
                :model-value="emailCampaign.transactional_email_api_endpoint"
                placeholder="Please save the campaign to get the custom API endpoint"
                readonly
                class="rounded-l-none" />
            </div>
            <Tooltip content="Copy URL">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                class="ml-1"
                @click="copyTransactionalEmailApiEndpoint">
                <Copy
                  :stroke-width="1.5"
                  class="size-3.5" />
              </Button>
            </Tooltip>
          </div>
        </FormItem>
        <div class="flex justify-between pt-4">
          <Button
            type="button"
            :variant="hasErrors ? 'destructive' : 'default'"
            :disabled="!hasCampaignChanged || !isCampaignEditable || hasErrors"
            @click="onSubmit">
            Save
          </Button>
          <div
            v-if="!isNewCampaign"
            class="flex gap-2">
            <Button
              type="button"
              variant="outline"
              :disabled="!isCampaignEditable || hasCampaignChanged"
              @click="handleTest">
              Send test
            </Button>
            <Button
              v-if="isBatchCampaign"
              type="button"
              :disabled="(!isCampaignEditable && !isSuspendedCampaign) || !hasScheduledDate || hasCampaignChanged"
              @click="handleSchedule">
              Schedule
            </Button>
            <Button
              v-if="isBatchCampaign"
              type="button"
              variant="outline"
              :disabled="hasCampaignChanged"
              @click="handlePush">
              Push without scheduling
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
    <Dialog v-model:open="isTestDialogVisible">
      <DialogContent class="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Send test email</DialogTitle>
        </DialogHeader>
        <div class="flex flex-col gap-2 py-2">
          <p class="text-sm text-muted-foreground">You can choose a test contact to check the email template</p>
          <Select
            v-model="testContactToken"
            :options="getContactOptions"
            clearable
            placeholder="Select a contact" />
        </div>
        <DialogFooter>
          <Button @click="sendTest">Send test</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog v-model:open="isSurveyNotLiveDialogVisible">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>The survey is currently not 'Live'</AlertDialogTitle>
          <AlertDialogDescription>
            Don't forget to set it to 'Live' before the campaign is sent.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction @click="navigateToCampaigns">Ok</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </OnePanePage>
</template>
